import type { ElementType } from 'react';

import { InMemoryCache } from '@apollo/client';
import type { AiCoach_Action as AiCoachAction } from '__generated__/graphql-types';
import { ApolloClient } from 'apollo-client';

import { getIetfLanguageTag } from 'js/lib/language';

import type {
  AiCoach_CreateUserMessageAndReturnResponseMutation as CreateUserMessageMutation,
  AiCoach_CreateUserMessageAndReturnResponseMutationVariables as CreateUserMessageMutationVariables,
  CreateUserMessageResultFragment,
} from 'bundles/ai-course-coach/queries/__generated__/aiCoachCreateUserMessageAndReturnResponseMutation';
import aiCoachCreateUserMessageAndReturnResponseMutation from 'bundles/ai-course-coach/queries/aiCoachCreateUserMessageAndReturnResponseMutation.graphql';

import store from '.';
import { persistantChatSessionId } from '../utils/persistantChatSessionId';
import * as actions from './actions';
import type { UpdateChatHistory } from './actions/types';
import type { AiCoachRecommendedAction, Feedback, LinkParams } from './types';

export function updateChatHistory(messages: UpdateChatHistory['messages']) {
  const action = actions.updateChatHistory(messages);
  store.dispatch(action);
}

export function addUserMessage(text: string, id?: string, timestamp?: string, action?: AiCoachAction) {
  store.dispatch(actions.addUserMessage(text, id, timestamp, action));
}

interface AddResponseMessage {
  text: string;
  id?: string;
  clipRecommendations?: CreateUserMessageResultFragment['clipRecommendations'];
  userFeedback?: Feedback | null;
  timestamp?: string;
  recommendedActions?: AiCoachRecommendedAction;
}

export function addResponseMessage({
  text,
  id,
  clipRecommendations,
  userFeedback,
  timestamp,
  recommendedActions,
}: AddResponseMessage) {
  store.dispatch(
    actions.addResponseMessage(text, id, clipRecommendations, userFeedback, timestamp, recommendedActions)
  );
}

export function addLinkSnippet(link: LinkParams, id?: string) {
  store.dispatch(actions.addLinkSnippet(link, id));
}

export function enableMsgLoader(enabledState: boolean) {
  store.dispatch(actions.enableMsgLoader(enabledState));
}

export function renderCustomComponent(component: ElementType, props: any, showAvatar = false, id?: string) {
  store.dispatch(actions.renderCustomComponent(component, props, showAvatar, id));
}

export function toggleWidget() {
  store.dispatch(actions.toggleChat());
}

export function openCoachWidget() {
  store.dispatch(actions.openCoachWidget());
}

export function closeCoachWidget() {
  store.dispatch(actions.closeCoachWidget());
}

export function toggleFullScreen() {
  store.dispatch(actions.toggleFullScreen());
}

export function toggleInputDisabled() {
  store.dispatch(actions.toggleInputDisabled());
}

export function dropMessages() {
  store.dispatch(actions.dropMessages());
}

export function deleteMessages(count: number, id?: string) {
  store.dispatch(actions.deleteMessages(count, id));
}

export function markAllAsRead() {
  store.dispatch(actions.markAllMessagesRead());
}

export function setBadgeCount(count: number) {
  store.dispatch(actions.setBadgeCount(count));
}

export function setUserInputText(inputText: string) {
  store.dispatch(actions.setUserInput(inputText));
}

export function clearUserInputText() {
  store.dispatch(actions.setUserInput(''));
}

export function setChatSessionId(chatSessionId?: string) {
  store.dispatch(actions.setChatSessionId(chatSessionId));
}

export function setChatSessionStorageKey(chatSessionStorageKey: string) {
  store.dispatch(actions.setChatSessionStorageKey(chatSessionStorageKey));
}

export function clearChatSession() {
  const { sessionState } = store.getState();
  const { chatSessionStorageKey: chatSessionStorageKeyState } = sessionState;
  persistantChatSessionId.clear(chatSessionStorageKeyState?.chatSessionStorageKey);
  store.dispatch(actions.clearChatSession());
}

// implement a functional approach to calling apollo mutation and dispatching the result
export async function sendMessage(variables: CreateUserMessageMutationVariables) {
  const { sessionState } = store.getState();
  const { chatSessionId: chatSessionIdState, chatSessionStorageKey: chatSessionStorageKeyState } = sessionState;
  const cache = new InMemoryCache();
  const client = new ApolloClient({
    uri: `/graphql-gateway?opname=AiCoach_CreateUserMessageAndReturnResponse`,
    cache,
    headers: {
      'Accept-Language': getIetfLanguageTag(),
    },
  });

  addUserMessage(variables.input.message, undefined, undefined, variables.input.action);
  enableMsgLoader(true);
  return client
    .mutate<CreateUserMessageMutation>({
      mutation: aiCoachCreateUserMessageAndReturnResponseMutation,
      variables: {
        input: { ...variables.input, chatSessionId: chatSessionIdState },
        clipRecommendationsEnabled: variables.clipRecommendationsEnabled,
        recommendedActionsEnabled: variables.recommendedActionsEnabled,
      },
      context: { clientName: 'gatewayGql' },
    })
    .then(({ data }) => {
      const { chatSessionId, messageByAiCoach, clipRecommendations, recommendedActions, navigationButtons } =
        data?.AiCoach_CreateUserMessageAndReturnResponse || {};

      enableMsgLoader(false);

      if (!chatSessionIdState) {
        setChatSessionId(chatSessionId);
      }

      const currentChatSessionId = persistantChatSessionId.get(chatSessionStorageKeyState?.chatSessionStorageKey);
      if (!currentChatSessionId && chatSessionId) {
        persistantChatSessionId.set(chatSessionStorageKeyState?.chatSessionStorageKey, chatSessionId);
      }

      if (messageByAiCoach?.message) {
        store.dispatch(
          actions.addResponseMessage(
            messageByAiCoach.message,
            messageByAiCoach.messageId,
            clipRecommendations,
            undefined,
            '',
            recommendedActions,
            navigationButtons
          )
        );
      }
      return data;
    });
}
